import type { ReactNode } from 'react';
import type { LucideIcon } from 'lucide-react';
import { Link, useLocation } from 'react-router-dom';
import { cn } from '../ui/cn';
import { NavMenuIconLabel } from './NavMenuIconLabel';
import { iconForGroup, iconForModule, iconForPortalOps, iconForSubModule } from './navIcons';
import {
  modulesConfig,
  portalOpsConfig,
  railModel,
  subModuleOwnsPath,
  subModulesAsPageTabs,
  subModulesForModule,
  type ModuleDef,
  type ModuleId,
  type PortalOpsId,
} from './modulesConfig';
import { SHELL_RAIL_WIDTH_CLASS } from './shellLayout';

/**
 * Tier-1 portal navigation — the "where am I" axis (PRO-0340 / ADR-014 Amendment 1;
 * nested deep modules ADR-014 Amendment 3; desktop-only after PRO-0350). Renders on a
 * dedicated `--bg-sidebar` chrome band at `md+` with pill active states. Mobile tier-1
 * is handled by `ModuleNav`; nested sub-modules on mobile use `ModuleSubModuleNav`.
 *
 * The active module is route-derived (`moduleForPath`), not an exact route match, so
 * links use plain `Link` with `aria-current` from `activeModuleId`.
 */
export const moduleLinkClasses = (isActive: boolean) =>
  cn(
    'flex min-h-11 items-center whitespace-nowrap rounded-md border-l-2 px-3 py-2 text-sm no-underline transition-colors ease-ds',
    isActive
      ? 'border-primary bg-workspace font-semibold text-text-primary shadow-sm'
      : 'border-transparent font-normal text-text-secondary hover:bg-workspace/70',
  );

const subModuleLinkClasses = (isActive: boolean) =>
  cn(
    'flex min-h-11 items-center whitespace-nowrap rounded-md border-l-2 px-3 py-2 text-sm no-underline transition-colors ease-ds',
    isActive
      ? 'border-primary bg-workspace font-semibold text-text-primary'
      : 'border-transparent font-normal text-text-secondary hover:bg-workspace/70',
  );

export function ModuleRail({
  activeModuleId,
  complaintsEnabled = false,
  showPortalOps = false,
  activePortalOpsId = null,
  modules = modulesConfig,
  navRestructureEnabled = false,
  suppressActiveModule = false,
  profileSettingsEnabled = false,
}: {
  activeModuleId: ModuleId;
  complaintsEnabled?: boolean;
  showPortalOps?: boolean;
  activePortalOpsId?: PortalOpsId | null;
  /** Flag-filtered module set (PRO-0590 `visibleModules`); defaults to the full registry. */
  modules?: readonly ModuleDef[];
  /**
   * PRO-0740 / ADR-014 amendment (SF-2). When true the re-homed Referrals My Coverage
   * / Service Profiles sub-modules are dropped from the rail nested list (6 -> 4).
   */
  profileSettingsEnabled?: boolean;
  /**
   * PRO-0600 / ADR-014 A8. Off (default) → the current flat rail byte-for-byte;
   * on → the scope-based grouped rail (`railModel`). Groups render as labelled
   * NON-interactive sections inside the single "Modules" landmark (TD-3 / MF-02).
   */
  navRestructureEnabled?: boolean;
  /**
   * PRO-0740 / ADR-014 amendment (MF-2). When true, NO tier-1 module is marked
   * active even though `moduleForPath` falls back to `'comms'` for the non-module
   * `/account` route — mirrors the `activePortalOpsId` suppression so the account
   * page does not falsely highlight Communications.
   */
  suppressActiveModule?: boolean;
}) {
  const { pathname } = useLocation();

  // Render one module row (link + nested sub-modules when active) — reused for both
  // flat entries and group members so the flag-off DOM is byte-for-byte unchanged.
  const renderModuleItem = (module: ModuleDef): ReactNode => {
    // MF-001: on a portal-ops route (`/platform/*`) no business module may show as
    // active, even though `moduleForPath` falls back to `'comms'`.
    // Active = the module OWNS the current path (route-derived `activeModuleId`), not an
    // exact `basePath` match — otherwise a module whose landing redirects to a sub-route
    // (Insights → a lens) or whose in-module nav lives elsewhere (Comms tier-2, Complaints
    // tabs) would never highlight. Mirrors the mobile drawer (`ModuleLauncher`).
    const isModuleActive = !activePortalOpsId && !suppressActiveModule && module.id === activeModuleId;
    const subModules = subModulesForModule(module, complaintsEnabled, profileSettingsEnabled);
    // Page-tab modules (functional Complaints; grouped nested members like Complaints
    // and Referrals) surface their sub-modules as the tier-2 strip (RoutedAppShell),
    // so the rail carries no nested list — otherwise the links would appear twice
    // (PRO-0600 follow-up).
    const showNested =
      module.navShape === 'nested' &&
      isModuleActive &&
      subModules.length > 0 &&
      !subModulesAsPageTabs(module, complaintsEnabled, navRestructureEnabled, profileSettingsEnabled);

    return (
      <li key={module.id}>
        <Link
          to={module.basePath}
          aria-current={isModuleActive ? 'page' : undefined}
          className={moduleLinkClasses(isModuleActive)}
        >
          <NavMenuIconLabel icon={iconForModule(module.id)} active={isModuleActive}>
            {module.label}
          </NavMenuIconLabel>
        </Link>
        {showNested ? (
          <ul className="m-0 ml-3 mt-1 flex list-none flex-col gap-0.5 p-0">
            {subModules.map((sub) => {
              const isSubActive = subModuleOwnsPath(sub, pathname, module.id, complaintsEnabled);
              return (
                <li key={sub.id}>
                  <Link
                    to={sub.basePath}
                    aria-current={isSubActive ? 'page' : undefined}
                    className={subModuleLinkClasses(isSubActive)}
                  >
                    <NavMenuIconLabel icon={iconForSubModule(sub.id)} active={isSubActive}>
                      {sub.label}
                    </NavMenuIconLabel>
                  </Link>
                </li>
              );
            })}
          </ul>
        ) : null}
      </li>
    );
  };

  // Shared labelled, NON-interactive scope header (`<p>`, no link, no aria-current)
  // used by BOTH group sections (School Ops / Commercial) and standalone-nested
  // sections (Human Resources / Finance), so every scope header reads identically.
  const renderScopeHeader = (labelId: string, Icon: LucideIcon, label: string) => (
    <p
      id={labelId}
      className="mb-1 mt-2 flex items-center gap-2 whitespace-nowrap px-3 text-xs font-semibold uppercase tracking-wide text-text-secondary"
    >
      <Icon aria-hidden className="h-4 w-4 shrink-0" />
      {label}
    </p>
  );

  const entries = railModel(modules, navRestructureEnabled);

  return (
    <div
      className={cn(
        'hidden w-full shrink-0 md:flex md:flex-col md:self-stretch md:border-r md:border-border md:bg-sidebar md:px-3 md:py-4',
        SHELL_RAIL_WIDTH_CLASS,
      )}
    >
      <nav
        id="module-rail"
        aria-label="Modules"
        className="flex flex-1 flex-col md:mx-0 md:mb-0 md:mt-0 md:border-0 md:bg-transparent md:p-0"
      >
        <p className="mb-2 whitespace-nowrap px-3 text-xs font-semibold uppercase tracking-wide text-text-secondary">
          Modules
        </p>
        <ul className="m-0 flex list-none flex-col gap-1 p-0">
          {entries.map((entry) => {
            if (entry.kind === 'module') {
              return renderModuleItem(entry.module);
            }
            if (entry.kind === 'section') {
              // Standalone nested module (HR/Finance) as a scope header: the module
              // dashboard is NOT a link — its sub-modules are the always-visible
              // interactive links, uniform with a group section. The `ml-3` indent
              // visually nests the links under the header (interactive depth 2, MF-02).
              const labelId = `rail-section-${entry.module.id}`;
              return (
                <li key={`section-${entry.module.id}`}>
                  {renderScopeHeader(labelId, iconForModule(entry.module.id), entry.module.label)}
                  <ul
                    aria-labelledby={labelId}
                    className="m-0 ml-3 flex list-none flex-col gap-0.5 p-0"
                  >
                    {entry.subModules.map((sub) => {
                      const isSubActive = subModuleOwnsPath(
                        sub,
                        pathname,
                        entry.module.id,
                        complaintsEnabled,
                      );
                      return (
                        <li key={sub.id}>
                          <Link
                            to={sub.basePath}
                            aria-current={isSubActive ? 'page' : undefined}
                            className={subModuleLinkClasses(isSubActive)}
                          >
                            <NavMenuIconLabel icon={iconForSubModule(sub.id)} active={isSubActive}>
                              {sub.label}
                            </NavMenuIconLabel>
                          </Link>
                        </li>
                      );
                    })}
                  </ul>
                </li>
              );
            }
            // Labelled NON-interactive group section (no link, no aria-current) with a
            // nested, indented list of member module links; interactive depth stays 2 (MF-02).
            const labelId = `rail-group-${entry.group.id}`;
            return (
              <li key={`group-${entry.group.id}`}>
                {renderScopeHeader(labelId, iconForGroup(entry.group.id), entry.group.label)}
                <ul
                  aria-labelledby={labelId}
                  className="m-0 ml-3 flex list-none flex-col gap-1 p-0"
                >
                  {entry.members.map((member) => renderModuleItem(member))}
                </ul>
              </li>
            );
          })}
        </ul>
      </nav>
      {showPortalOps ? (
        <nav aria-label="Platform" className="mt-auto pt-4">
          <div aria-hidden className="mb-2 border-t border-border" />
          <ul className="m-0 flex list-none flex-col gap-1 p-0">
            {portalOpsConfig.map((ops) => {
              const isActive = activePortalOpsId === ops.id;
              return (
                <li key={ops.id}>
                  <Link
                    to={ops.defaultPath}
                    aria-current={isActive ? 'page' : undefined}
                    className={moduleLinkClasses(isActive)}
                  >
                    <NavMenuIconLabel icon={iconForPortalOps(ops.id)} active={isActive}>
                      {ops.label}
                    </NavMenuIconLabel>
                  </Link>
                </li>
              );
            })}
          </ul>
        </nav>
      ) : null}
    </div>
  );
}
